import { createContext, useContext, useEffect, useState } from "react";
import { HashDisplay } from "./Common";
import { useQuery } from "@tanstack/react-query";
import { TwinClient } from "../lib/client";


export function useTwinPersona(twinUrl) {
  let info = useQuery({
    queryKey: [ "GET", twinUrl, "/info" ],
    queryFn: async function getInfoQueryFn() {
      if (!twinUrl) return null;
      return await TwinClient.info(twinUrl);
    },

    retry: (n, err) => {
      if (err?.data?.status == 404) {
        return false;
      }
      return n < 3
    },

    enabled: !!twinUrl,
  });

  let persona = useQuery({
    queryKey: ["GET", twinUrl, "/persona"],
    queryFn: async function getPersonaFn() {
      if (!twinUrl) return null;
      let res = await TwinClient.persona(twinUrl);
      let info = res?.instances[0]?.info || res?.root?.info;
      try {
        return JSON.parse(info);
      } catch (_) {
        return info;
      }
    },

    retry: (n, err) => {
      if (err?.data?.status == 404) {
        return false;
      }
      return n < 3
    },

    enabled: !!twinUrl && !!info.data,
  });

  return { info, persona }
}

export function usePersona(twinUrl) {
  let { data: persona } = useQuery({
    queryKey: ["GET", twinUrl, "/persona"],
    queryFn: async function getPersonaFn() {
      if (!twinUrl) return null;
      let res = await TwinClient.persona(twinUrl);
      let info = res?.instances[0]?.info || res?.root?.info;
      try {
        return JSON.parse(info);
      } catch (_) {
        return info;
      }
    },
    enabled: !!twinUrl,
  });
  return persona;
}

export function AddressCard({ to, from }) {
  const h = to || from;
  const [isEditing, setIsEditing] = useState(false);
  const { getAlias, setAlias } = useAddressAlias();

  function AliasDisplay({ alias }) {
    let image = to ? "bg-[url(/public/img/user-arrow-in.svg)]" : "bg-[url(/public/img/user-arrow-out.svg)]"
    return <div className="flex flex-row">
      <div className={`size-[28px] aspect-square bg-left bg-no-repeat ${image}` }></div>
      <div className="ml-2 h-full text-left">
        {alias}
      </div>
    </div>
  }

  function AddressDisplay({ h }) {
    const alias = getAlias(h);
    return <div className="rounded max-w-fit">
      { alias ?
        <div className="flex flex-col">
          <div title="Edit Alias"
            onClick={() => setIsEditing(true)}
            className="h-3/4 cursor-pointer">
            <AliasDisplay alias={alias} />
          </div>
          <div className="h-1/4 pt-1">
            <HashDisplay h={h} fontSize={"12px"} />
          </div>
        </div>

        :

        <div className="flex flex-row group/address" >
          <div className="flex-1 w-[85%] h-full">
           <HashDisplay h={h} fontSize={"14px"} />
          </div>
        </div>
      }
    </div>
  }

  function AliasInput({ h }) {

    function handleKeyDown(e) {
      if (e.key == "Escape") {
        try {
          e.target.form.reset(); // this is new here
          e.target.blur();
          setIsEditing(false);
        } catch (e) {
          console.warn("Error in Esc key handler", e);
        }
      }

      if (e.key == "Enter") {
        try {
          // let event = new Event("submit", {cancelable:true})
          e.target.form.requestSubmit()
          e.preventDefault()
        } catch (e) {
          console.warn("Error in Enter key handler", e);
        }
      }
      return; // must return otherwise the key is not propagated
    }

    function submitAlias(e) {
      e.preventDefault(); // figure out a way to re-render component without refreshing the page
      let formData = new FormData(e.target);
      let newAlias = formData.get("alias")
      setAlias(h, newAlias);
      e.target.elements.alias.value = newAlias
      e.target.elements.alias.blur()
      setIsEditing(false)
      return
    }

    function resetAlias(e) {
      setAlias(h, null);
      e.target.form.elements.alias.value = null
      setIsEditing(false);
    }

    return (
      <div className="max-w-fit text-black">
        <form onSubmit={submitAlias}
          className="flex flex-col p-2 rounded bg-slate-200" >
          <div className="flex flex-row min-w-fit">
            <div className="border border-black flex flex-row rounded min-w-fit bg-white focus-within:outline outline-2 outline-blue-600 "

            >
              <textarea
                autoFocus
                name="alias"
                placeholder="Input Hash Alias"
                defaultValue={getAlias(h)}

                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(() => setIsEditing(false), 250)}
                // NOTE(sfertman): to give resetAlias a chance to fire
                className="px-2 max-h-min text-wrap focus:outline-none bg-transparent"
              />
              <button
                type="button"
                title="Reset alias"
                onClick={resetAlias}
                className="px-[6px] rounded-r h-full align-middle text-start text-red-600 hover:text-white hover:bg-red-500">
                {"\u00D7"}
              </button>
            </div>
          </div>
          <div className="pt-1 max-w-fit">
            <HashDisplay h={h} fontSize={"12px"} />
          </div>
        </form>
      </div>
    )
  }
  return (<div className="">

    {isEditing ? <AliasInput h={h} /> : <AddressDisplay h={h} />}
  </div>);
}

export const AddressAliasContext = createContext();
export function AddressAliasContextProvider(props) {

  const [aliases, setAliases] = useState(() => {
    const stored = localStorage.getItem("aliases");
    return stored ? JSON.parse(stored) : {};
  });

  useEffect(() => {
    localStorage.setItem("aliases", JSON.stringify(aliases));
  });

  function getAlias(address) {
    return aliases[address];
  }

  function setAlias(address, alias) {
    return setAliases(oldval => ({...oldval, [address]: alias}));
  }

  return <AddressAliasContext.Provider value={{ getAlias, setAlias }}>
    {props.children}
  </AddressAliasContext.Provider>
}

export function useAddressAlias() {
  return useContext(AddressAliasContext);
}
